<template>
    <layout>
        <main class="system-page">
            <section class="header">
                <h2>3. Define Brace Type and Piping System</h2>
                <p>
                    Configure your main and branch lines below.
                </p>
                <div v-if="!hasAgency && designId" class="required">
                    AGENCY SELECTION REQUIRED: Please go back to
                    <router-link :to="{ name: 'submittal-setup', params: {id: designId}}">Seismic Design Setup</router-link>
                    and select an agency before proceeding for accurate options here.
                </div>
            </section>
            <section>
                <h3>Define Brace Orientation</h3>
                <p class="note">
                    Select one type of brace
                </p>
                <fieldset>
                    <div class="radio flex">
                        <div>
                            <div class="help">
                                <i class="fa fa-search-plus inline-popup" data-mfp-src="#lateral-help"></i>
                            </div>
                            <input type="radio" id="lateral" name="orientation" v-model="record.orientation" value="lateral">
                            <label for="lateral">
                                <img src="/img/seismic/lateral-brace.jpg" alt="Lateral Brace" />
                                <span class="radiobtn">Lateral</span>
                            </label>
                        </div>

                        <div>
                            <div class="help">
                                <i class="fa fa-search-plus inline-popup" data-mfp-src="#longitudinal-help"></i>
                            </div>
                            <input type="radio" id="longitudinal" name="orientation" v-model="record.orientation" value="longitudinal">
                            <label for="longitudinal">
                                <img src="/img/seismic/longitudinal-brace.jpg" alt="Longitudinal Brace" />
                                <span class="radiobtn">Longitudinal</span>
                            </label>
                        </div>

                        <div>
                            <div class="help">
                                <i class="fa fa-search-plus inline-popup" data-mfp-src="#lat-long-help"></i>
                            </div>
                            <input type="radio" id="lateral-brace" name="orientation" v-model="record.orientation" value="lateral-longitudinal">

                            <label for="lateral-brace">
                                <img src="/img/seismic/lat-long-brace.jpg" alt="Lateral Brace as a Lateral & Longitudinal Brace" />
                                <span class="radiobtn">Lateral Brace as a Lateral &amp; Longitudinal Brace</span>
                            </label>
                        </div>

                        <div>
                            <div class="help">
                                <i class="fa fa-search-plus inline-popup" data-mfp-src="#long-lat-help"></i>
                            </div>
                            <input type="radio" id="long-lat-brace" name="orientation" v-model="record.orientation" value="longitudinal-lateral">

                            <label for="long-lat-brace">
                                <img src="/img/seismic/long-lat-brace.jpg" alt="Longitudinal Brace as a Lateral & Longitudinal Brace" />
                                <span class="radiobtn">Longitudinal Brace as a Lateral &amp; Longitudinal Brace</span>
                            </label>
                        </div>

                        <div>
                            <div class="help">
                                <i class="fa fa-search-plus inline-popup" data-mfp-src="#riser-wall-help"></i>
                            </div>
                            <input type="radio" id="riser-wall" name="orientation" v-model="record.orientation" value="riser-wall">
                            <label for="riser-wall">
                                <img src="/img/seismic/riser-wall-brace.jpg" alt="Riser / 4-Way Brace Assembly - Wall Condition" />
                                <span class="radiobtn">Riser / 4-Way Brace Assembly - Wall Condition</span>
                            </label>
                        </div>

                        <div>
                            <div class="help">
                                <i class="fa fa-search-plus inline-popup" data-mfp-src="#riser-corner-help"></i>
                            </div>
                            <input type="radio" id="riser-corner" name="orientation" v-model="record.orientation" value="riser-corner">
                            <label for="riser-corner">
                                <img src="/img/seismic/riser-corner-brace.jpg" alt="Riser / 4-Way Brace Assembly - Corner Condition" />
                                <span class="radiobtn">Riser / 4-Way Brace Assembly - Corner Condition</span>
                            </label>
                        </div>
                    </div>
                </fieldset>

                <branch-line
                    v-for="branch in branches"
                    :uid="branch.uid"
                    :key="branch.uid"
                    :displayBranch="displayBranch"
                    :lateralLimit="lateralLimit"
                    :longitudinalLimit="longitudinalLimit"
                    >
                </branch-line>

                <div v-if="displayBranch">
                    <button @click="addBranch">Add A Branch Line</button>
                </div>

                <div class="totals">
                    <p>
                        Total ZOI System Weight:
                        <span v-if="totalZoiSystemWeight" v-text="lbOrKg(totalZoiSystemWeight, true, false, 2)"></span>
                        <span v-else>--</span>
                    </p>
                    <p>
                        NFPA System Multiplier: <span v-text="nfpaSystemMultiplier"></span>
                    </p>
                    <p>
                        ZOI System Design Weight, Wp:
                        <span v-if="wp" v-text="lbOrKg(wp, true, true, 2)"></span>
                        <span v-else>--</span>
                    </p>
                    <p>
                        ZOI Seismic Design Load, Fpw = Cp*Wp:
                        <span v-if="cpAndWp" v-text="lbfOrKn(cpAndWp, true, useMetric(2))"></span>
                        <span v-else>--</span>
                    </p>
                </div>
            </section>

            <div class="info" v-if="showDebugging">
                <h4>Testing variables</h4>
                <p>
                    Metric: <strong v-if="useMetric(2)">Yes</strong> <span v-else>No</span>
                </p>
                <div v-for="branch in compactBranches">
                    <div v-for="segment in branch.segments">
                        <hr>
                        <p>
                            Segment ID: <strong v-text="segment.id"/>
                        </p>
                        <p>
                            Pipe: <strong v-text="segment.pipeName"/> <strong v-text="segment.pipeType"/>
                        </p>
                        <p>
                            Lateral: <strong v-text="segment.lateralPipeLength"/>
                        </p>
                        <p>
                            Longitudinal: <strong v-text="segment.longitudinalPipeLength"/>
                        </p>
                        <p>
                            Pipe Length: <strong v-text="segment.pipeLength"/>
                        </p>
                    </div>
                </div>
            </div>

            <section>
                <actions
                    @next="saveThenNext"
                    @save="saveThenSummary"
                    @saveProgress="saveProgress"
                    :fadeSave="!hasChanges"
                    :fadeNext="!cpAndWp || hasRiserNippleErrors"
                    :saving="saving"
                />
            </section>
        </main>
        <aside class="primary">
            <sidebar :design="this.designRecord" :brace="this.record"></sidebar>
        </aside>
        <div id="lateral-help" class="inline-modal mfp-hide">
            <img src="/img/seismic/lateral-brace.jpg" alt="Lateral Brace" />
        </div>
        <div id="longitudinal-help" class="inline-modal mfp-hide">
            <img src="/img/seismic/longitudinal-brace.jpg" alt="Longitudinal Brace" />
        </div>
        <div id="lat-long-help" class="inline-modal mfp-hide">
            <img src="/img/seismic/lat-long-brace.jpg" alt="Lateral Brace as a Lateral & Longitudinal Brace" />
        </div>
        <div id="long-lat-help" class="inline-modal mfp-hide">
            <img src="/img/seismic/long-lat-brace.jpg" alt="Longitudinal Brace as a Lateral & Longitudinal Brace" />
        </div>
        <div id="riser-wall-help" class="inline-modal mfp-hide">
            <img src="/img/seismic/riser-wall-brace.jpg" alt="Riser / 4-Way Brace Assembly - Wall Condition" />
        </div>
        <div id="riser-corner-help" class="inline-modal mfp-hide">
            <img src="/img/seismic/riser-corner-brace.jpg" alt="Riser / 4-Way Brace Assembly - Corner Condition" />
        </div>
    </layout>
</template>
<script>
import ClockvineMixin from 'vue/common/clockvine-mixin';
import DesignLoader from './mixins/submittal-design-loader';

import Layout from './layout';
import Sidebar from './shared/sidebar';
import BranchLine from './shared/branch-line';
import Errors from './shared/errors';
import Actions from './shared/actions';

import { mapGetters, mapState } from 'vuex';
import { includes } from 'lodash';
import { contains } from 'jquery';
// import ShortUniqueId from 'short-unique-id';
// import axios from 'axios';

export default {
    components: {
        Layout,
        Sidebar,
        BranchLine,
        Errors,
        Actions
    },
    data() {
        return {
            type: 'submittalBrace',
            record: {
                orientation: 'lateral',
            },
            showDebugging: false,
        };
    },
    watch: {
        orientation: {
            handler(newVal, oldVal){
                this.$store.dispatch('submittalSystem/setOrientation', this.record.orientation);
            }
        },
        cp: {
            handler(){
                this.$store.dispatch('submittalSystem/setCp', this.record.cp);
            }
        },
    },
    mounted() {
        this.$store.dispatch('submittalSystem/fetchAll', { id: this.id });
        this.$store.dispatch('submittalSystem/resetSegments');

        var $popup = $('.inline-popup');
        if ($popup.length) {
            $popup.magnificPopup({
                type:'inline'
            });
        }
    },
    computed: {
        cp() {
            // this method is for the sidebar component
            return this.record.cp;
        },
        name() {
            return this.record.name;
        },
        orientation() {
            return this.record.orientation;
        },
        drawingReference() {
            return this.record.drawingReference;
        },
        displayBranch() {
            if (['lateral', 'lateral-longitudinal', 'longitudinal-lateral'].includes(this.record.orientation)) {
                return true;
            }

            return false;
        },
        hasErrors() {
            if (this.systemErrors.maxLoad) {
                return true;
            }

            // Loop over all the lat-long and long-lat segments to see if any violate the orientation size rule
            let sizeError = false;

            if (['lateral-longitudinal', 'longitudinal-lateral'].includes(this.record.orientation)) {
                let primary = null;
                let secondary = null;

                this.compactBranches.forEach((b) =>
                {
                    b.segments.forEach((s) => {
                        primary = Number(s.pipeSize);
                        secondary = Number(s.secondaryPipeSize);
                        
                        if (secondary > primary) {
                            sizeError = true;
                        }
                    });
                });
            }

            if (sizeError) {
                return true;
            }
            
            /**
            -------------------------------
            2024-12-20
            Commenting out this entire block because Charlie and John decided we don't need to 
            implement any restrictions on one direction being longer than the other. 
            
            If they change their minds again we can uncomment it. This works in conjunction with
            the lengthError computed property on the lat-log-segment.vue component so if you uncomment
            this block you'll also need to uncomment the code in the other component as well.

            At some point in the future, you may delete the commented out code at your discretion.
            If you do so, please delete the lengthError method and the div that displays the error
            from the lat-long-segment.vue component as well.
            -------------------------------

            // Loop over all the lat-long and long-lat segments to see if any violate the orientation length rule
            let lengthError = false;
            if ('lateral-longitudinal' == this.record.orientation) {
                let lateral = null;
                let longitudinal = null;

                this.compactBranches.forEach((b) =>
                {
                    b.segments.forEach((s) => {
                        lateral = Number(s.lateralPipeLength);
                        longitudinal = Number(s.longitudinalPipeLength);
                        
                        if (longitudinal > lateral) {
                            lengthError = true;
                        }
                    });
                });
            }
            if ('longitudinal-lateral' == this.record.orientation) {
                let lateral = null;
                let longitudinal = null;

                this.compactBranches.forEach((b) =>
                {
                    b.segments.forEach((s) => {
                        lateral = Number(s.lateralPipeLength);
                        longitudinal = Number(s.longitudinalPipeLength);

                        if (lateral > longitudinal) {
                            lengthError = true;
                        }
                    });
                });
            }

            if (lengthError) {
                return true;
            }
            /**/

            // Commented out because store state not updating so it causes validation errors
            // See TODO in the validateThenSave() method for this component
            // if (this.mainBranchLengthErrors.length > 0) {
                // console.log('hasErrors: mainBranchLengthErrors')
                // return true;
            // }
            return false;
        },
        lateralLimit() {
            let max = 40;

            this.compactBranches[0].segments.forEach((s) => {
                if (s.lateralPipeLength) {
                    max = max - s.lateralPipeLength;
                }
            });

            return max;
        },
        longitudinalLimit() {
            let max = 80;

            this.compactBranches[0].segments.forEach((s) => {
                if (s.longitudinalPipeLength) {
                    max = max - s.longitudinalPipeLength;
                }
            });

            return max;
        },
        hasAgency() {
            return this.designRecord ? this.designRecord.agency : '';
        },
        ...mapGetters({
            'compactBranches': 'submittalSystem/compactBranches',
            'hasRiserNippleErrors': 'submittalSystem/hasRiserNippleErrors',
            "mainBranchLengthErrors": "submittalSystem/mainBranchLengthErrors",
            'systemErrors' : 'submittalSystem/systemErrors',
        }),
        ...mapState({
            'branches': state => state.submittalSystem.branches,
            'cpAndWp':state => state.submittalSystem.zoiSeismicDesignLoad,
            'mainPipeType': state => state.submittalSystem.mainPipeType,
            'maxFpw': state => state.submittalSystem.maxFpw,
            'nfpaSystemMultiplier': state => state.submittalSystem.nfpaSystemMultiplier,
            'systemMaterial': state => state.submittalSystem.systemMaterial,
            'totalZoiSystemWeight': state => state.submittalSystem.totalZoiSystemWeight,
            'wp': state => state.submittalSystem.zoiSystemDesignWeightWp,
        }),
    },
    mixins: [
        ClockvineMixin,
        DesignLoader,
    ],
    methods: {
        addBranch() {
            this.$store.dispatch('submittalSystem/addBranch');
        },
        isDirty() {
            // always assume data is dirty
            return true;
        },
        getMaxFpw() {
            this.$store.dispatch('submittalSystem/getMaxFpwLoad');
        },
        getMaxLoadError() {
            this.$store.dispatch('submittalSystem/fetchMaxLoadError');
        },
        validateThenSave() {
            this.saving = true;

            return this.$store.dispatch('submittalSystem/fetchMaxLoadError')
                .then(() => {
                    // TODO: If we can we find a way to update the segment lengths
                    // so we can uncomment the length validation in hasErrors() 
                    // this.$store.dispatch('submittalSystem/setSegment', segmentData);
                }).then(() => {
                    if (!this.hasErrors) {
                        return this.save();
                    }
                    this.saving = false;
                    return Promise.reject();
                })
                .catch(() => {
                    this.saving = false
                    return Promise.reject();
                });
        },
        saveProgress() {
            this.validateThenSave();
        },
        saveThenSummary() {
            if (!this.cpAndWp || this.hasRiserNippleErrors) {
                return;
            }

            this.validateThenSave().then(() => {
                this.$router.push({name: 'submittal-brace-summary', params: { id: this.designId }});
            });
        },
        saveThenNext() {
            if (!this.cpAndWp || this.hasRiserNippleErrors) {
                return;
            }

            this.validateThenSave().then(() => {
                this.$router.push({name: 'submittal-brace-attachment', params: {
                    id: this.record.id || this.$route.params.id,
                    designId: this.designId
                }});
            })
            .catch((r) => {
                // console.log('catch error in saveThenNext');
                // console.log(r);
            });
        },
        getUrlParams() {
            return { designId: this.$route.params.designId };
        },
        getRecordParams() {
            let record = Object.assign({}, this.record, {
                totalZoiSystemWeight: this.totalZoiSystemWeight,
                nfpaSystemMultiplier: this.nfpaSystemMultiplier,
                zoiSystemDesignWeight: this.wp,
                zoiSystemDesignLoad: this.cpAndWp,
                branches: this.compactBranches,
                mainPipeType: this.mainPipeType,
                systemMaterial: this.systemMaterial
            });

            return Object.assign({}, this.getUrlParams(), { data: record });
        },
    },
}
</script>